import React, { useState, useEffect, useRef } from 'react';
import api from '../../../services/api';
import { useAuth } from '../../../contexts/AuthContext';
import { formatCurrency } from '../../../utils/formatters';
import { Printer } from 'lucide-react';
import { printContent } from '../../../utils/print';

export default function RelatorioProducao() {
  const { user } = useAuth();
  const [filters, setFilters] = useState({ data_inicio: new Date().toISOString().split('T')[0], data_fim: new Date().toISOString().split('T')[0], rota_id: '', turno: '', status: 'Recebido,Em Produção' });
  const [rotas, setRotas] = useState([]);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const printRef = useRef(null);

  useEffect(() => { api.get('/rotas').then(r => setRotas(r.data)).catch(() => {}); }, []);

  const set = (k, v) => setFilters(f => ({ ...f, [k]: v }));

  const gerarRelatorio = async () => {
    setLoading(true);
    setError('');
    try {
      const params = new URLSearchParams({ data_inicio: filters.data_inicio, data_fim: filters.data_fim });
      if (filters.rota_id) params.set('rota_id', filters.rota_id);
      if (filters.turno) params.set('turno', filters.turno);
      if (filters.status) params.set('status', filters.status);
      const res = await api.get(`/relatorios/producao?${params}`);
      setData(res.data);
    } catch (err) { setError(err.userMessage || 'Erro ao gerar relatório'); }
    finally { setLoading(false); }
  };

  const inputCls = "rounded-lg border border-brand-border px-3 py-2 text-sm font-body focus:outline-none focus:ring-2 focus:ring-brand-accent-mid";

  return (
    <div data-testid="relatorio-producao">
      <div className="flex items-center justify-between mb-6">
        <h1 className="dashboard-heading text-2xl">Relatório de Produção</h1>
        {data && <button onClick={() => printContent('Relatório de Produção', printRef)} className="no-print flex items-center gap-2 py-2 px-4 rounded-lg btn-primary font-dashboard font-semibold text-sm" data-testid="print-btn"><Printer className="w-4 h-4" /> Imprimir</button>}
      </div>

      {/* Filters */}
      <div className="card-dashboard mb-6 no-print">
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-4">
          <div><label className="block text-xs text-brand-text-muted mb-1 font-body">Data Início *</label><input type="date" className={inputCls} value={filters.data_inicio} onChange={e => set('data_inicio', e.target.value)} /></div>
          <div><label className="block text-xs text-brand-text-muted mb-1 font-body">Data Fim *</label><input type="date" className={inputCls} value={filters.data_fim} onChange={e => set('data_fim', e.target.value)} /></div>
          <div><label className="block text-xs text-brand-text-muted mb-1 font-body">Rota</label>
            <select className={inputCls} value={filters.rota_id} onChange={e => set('rota_id', e.target.value)}>
              <option value="">Todas</option>
              {rotas.map(r => <option key={r.id} value={r.id}>{r.nome}</option>)}
            </select>
          </div>
          <div><label className="block text-xs text-brand-text-muted mb-1 font-body">Turno</label>
            <select className={inputCls} value={filters.turno} onChange={e => set('turno', e.target.value)}>
              <option value="">Ambos</option><option>Manhã</option><option>Tarde</option>
            </select>
          </div>
        </div>
        <button onClick={gerarRelatorio} disabled={loading} className="py-2.5 px-6 rounded-lg btn-primary font-dashboard font-semibold text-sm disabled:opacity-60" data-testid="gerar-relatorio-btn">
          {loading ? 'Gerando...' : 'Gerar Relatório'}
        </button>
        {error && <p className="text-red-600 text-sm mt-2">{error}</p>}
      </div>

      {/* Report */}
      {data && (
        <div ref={printRef} className="print-content">
          <div className="mb-6 print:mb-4">
            <h2 className="font-dashboard font-bold text-lg text-brand-text">Relatório de Produção</h2>
            <p className="text-sm text-brand-text-muted font-body">Período: {filters.data_inicio} a {filters.data_fim} · {data.total_pedidos} pedido(s)</p>
          </div>

          {/* Seção 1: Por cliente */}
          <h3 className="font-dashboard font-bold text-brand-text mb-4 border-b border-brand-border pb-2">Seção 1 — Lista por Cliente</h3>
          <div className="space-y-6 mb-8">
            {data.pedidos?.map(p => (
              <div key={p.id} className="page-break-avoid border border-brand-border rounded-lg p-4">
                <div className="mb-3">
                  <p className="font-dashboard font-bold text-brand-text">#{p.numero} — {p.cliente_nome}</p>
                  <p className="text-xs text-brand-text-muted font-body">{p.cliente_endereco} · {p.turno} · {p.prazo}</p>
                </div>
                <table className="w-full text-sm font-body">
                  <thead><tr className="border-b border-brand-border"><th className="text-left py-1 text-xs text-brand-text-muted font-dashboard">Produto</th><th className="text-center py-1 text-xs text-brand-text-muted font-dashboard">Qtd</th><th className="text-center py-1 text-xs text-brand-text-muted font-dashboard">Un</th></tr></thead>
                  <tbody>
                    {p.itens?.map((item, i) => (
                      <tr key={i} className="border-b border-brand-border last:border-0">
                        <td className="py-1">{item.descricao}</td>
                        <td className="py-1 text-center font-bold">{item.quantidade}</td>
                        <td className="py-1 text-center text-brand-text-muted">{item.unidade}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            ))}
          </div>

          {/* Seção 2: Consolidada */}
          <div className="page-break-before">
            <h3 className="font-dashboard font-bold text-brand-text mb-4 border-b border-brand-border pb-2">Seção 2 — Lista Geral para Produção</h3>
            <table className="w-full text-sm font-body border border-brand-border">
              <thead className="bg-brand-bg"><tr><th className="text-left py-2 px-3 font-dashboard text-xs">SKU</th><th className="text-left py-2 px-3 font-dashboard text-xs">Produto</th><th className="text-center py-2 px-3 font-dashboard text-xs">Qtd. Total</th><th className="text-center py-2 px-3 font-dashboard text-xs">Un.</th></tr></thead>
              <tbody>
                {data.produtos_consolidados?.map((p, i) => (
                  <tr key={i} className={`border-t border-brand-border ${i % 2 === 0 ? '' : 'bg-brand-bg/50'}`}>
                    <td className="py-2 px-3 font-mono text-xs text-brand-text-muted">{p.sku}</td>
                    <td className="py-2 px-3 font-medium text-brand-text">{p.descricao}</td>
                    <td className="py-2 px-3 text-center font-bold text-brand-primary">{p.quantidade_total}</td>
                    <td className="py-2 px-3 text-center text-brand-text-muted">{p.unidade}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
}
